// Third party
import React from 'react';
import { connect } from 'react-redux';
import { withFormik, FormikProps, Form } from 'formik';
import * as Yup from 'yup';
import moment from 'moment';

// Components
import { FormInput, FormError } from '../../forms';
import { PrimaryButton, PrimaryLink, IconButton, CancelButton } from '../../buttons';
import { RadioGroup, SelectInput, SelectOption } from '../../inputs';

// Utils
import { FormattedMessage, formatMessage } from '@/utils/i18n';
import { showError } from '@/utils/errors';
import notification from '@/utils/notification';
import { getAge } from '@/utils/time';
import { ACCOUNT_TYPES, REGISTERED_ACCOUNT_TYPES, PROVINCES } from '@/utils/constants';
import { BMO_CONTACT_FIELDS, POLICYOWNER_OPTIONS, BMO_CONTACT_TYPE, BMOi18n, BMOFormErrorKeys, maxAge71Accounts } from '@/utils/bmo-gif/constants';

import { getFilteredProvinces, switchRegisteredAccountType, getBaseAge } from '@/utils/bmo-gif/index';

// Actions
import { ReduxState } from '../../../../redux/modules/types';
import { saveContact } from '../../../../redux/modules/contacts';
import { Tooltip } from '../../tooltips';
import GenericContact from '@/types/generic-contact';
import AccountType from '@/types/account-type';
import RadioItem from '@/types/radio-item';

const dobRegex = /\d{2}\/\d{2}\/\d{4}$/;
const dobMask = '99/99/9999';

type DispatchProps = {
  saveContact: (contact: Partial<GenericContact>) => Promise<GenericContact>;
};

type StateProps = {
  locale: string;
};

type FormValues = {
  fname: string;
  lname: string;
  dob?: Date;
  ownerIsAnnuitant: boolean;
  fnamePolicyOwner?: string;
  lnamePolicyOwner?: string;
  fnameSecondPolicyOwner?: string;
  lnameSecondPolicyOwner?: string;
  province?: string;
  accountType?: AccountType;
  registeredAccountType?: string;
  showSecondPolicyOwner?: boolean;
};

type ParentProps = StateProps &
  DispatchProps & {
    compact?: boolean;
    contact: Partial<GenericContact>;
    onSave: (contact: GenericContact) => void;
    onCancel: () => void;
  };

type State = {
  dobInput?: string;
  showErrors?: boolean;
};

type Props = ParentProps & FormikProps<FormValues>;

class EditBMOContact extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    const {
      values: { dob },
    } = props;

    this.state = {
      dobInput: dob && moment(dob).format('DD/MM/YYYY'),
    };
  }

  renderAge = (dobInput?: string) => {
    if (!dobInput) {
      return '';
    }

    const validFormat = dobRegex.test(dobInput);

    const age = getAge(dobInput, 'DD/MM/YYYY');

    return validFormat && age ? age : '';
  };

  validateAccountType = () => {
    const {
      values: { accountType, registeredAccountType },
    } = this.props;

    if (accountType === ACCOUNT_TYPES.REGISTERED) {
      return !!registeredAccountType;
    }

    return !!accountType;
  };

  onCancel = () => {
    this.props.onCancel();
  };

  onSubmit = () => {
    this.setState({ showErrors: true });

    this.props.submitForm();
  };

  showSecondPolicyOwner = () => {
    this.props.setFieldValue(BMO_CONTACT_FIELDS.SHOW_SECOND_POLICYOWNER, true);
  };

  deleteSecondPolicyOwner = () => {
    this.props.setFieldValue(BMO_CONTACT_FIELDS.SECOND_POLICYOWNER_FIRST_NAME, undefined);
    this.props.setFieldValue(BMO_CONTACT_FIELDS.SECOND_POLICYOWNER_LAST_NAME, undefined);
    this.props.setFieldValue(BMO_CONTACT_FIELDS.SHOW_SECOND_POLICYOWNER, false);
  };

  onChangeDob = (field: string, value: string) => {
    if (field === BMO_CONTACT_FIELDS.DOB) {
      this.setState({ dobInput: value });
    }

    const validFormat = dobRegex.test(value);

    const date = moment(value, 'DD/MM/YYYY');

    if (validFormat) {
      this.props.setFieldValue(field, date.toDate());
    } else {
      this.props.setFieldValue(field, undefined);
    }
  };

  renderSeperatePolicyowner = () => {
    const {
      errors,
      locale,
      values: { ownerIsAnnuitant },
      compact,
    } = this.props;
    const { showErrors } = this.state;

    const policyOwnerOptions = Object.keys(POLICYOWNER_OPTIONS).map(option => ({
      value: POLICYOWNER_OPTIONS[option],
      label: BMOi18n[locale][option],
    }));

    const onChangePolicyowner = (value: boolean) => {
      if (value) {
        this.props.setFieldValue(BMO_CONTACT_FIELDS.POLICYOWNER_FIRST_NAME, undefined);
        this.props.setFieldValue(BMO_CONTACT_FIELDS.POLICYOWNER_LAST_NAME, undefined);
        this.props.setFieldValue(BMO_CONTACT_FIELDS.SECOND_POLICYOWNER_FIRST_NAME, undefined);
        this.props.setFieldValue(BMO_CONTACT_FIELDS.SECOND_POLICYOWNER_LAST_NAME, undefined);
        this.props.setFieldValue(BMO_CONTACT_FIELDS.SHOW_SECOND_POLICYOWNER, false);
      }

      this.props.setFieldValue(BMO_CONTACT_FIELDS.OWNER_IS_ANNUITANT, value);
    };

    return (
      <>
        <div className="owner-is-annuitant row" data-private data-dd-privacy="mask">
          <SelectInput
            horizontalLabel
            label={BMOi18n[locale].POLICYOWNER_ANNUITANT_OPTION}
            onValueChange={onChangePolicyowner}
            selected={ownerIsAnnuitant}
            className="is-annuitant-policyowner"
          >
            {policyOwnerOptions.map((option, i) => (
              <SelectOption key={`${i}-${option.value}`} value={option.value}>
                {option.label}
              </SelectOption>
            ))}
          </SelectInput>
        </div>

        {!ownerIsAnnuitant && (
          <div className="field-section policyowner-section">
            {compact && <h3>{BMOi18n[locale].POLICYOWNER_1}</h3>}

            <div>
              <div className="row seperate-policyowner" data-private data-dd-privacy="mask">
                <div className="col">
                  <FormInput
                    name={BMO_CONTACT_FIELDS.POLICYOWNER_FIRST_NAME}
                    label={BMOi18n[locale][compact ? 'FIRST_NAME' : 'POLICYOWNER_FIRST_NAME']}
                    error={showErrors && !!errors.fnamePolicyOwner}
                    placeholder="Jane"
                    required
                  />

                  {showErrors && errors.fnamePolicyOwner && (
                    <FormError name={BMO_CONTACT_FIELDS.POLICYOWNER_FIRST_NAME} message={BMOi18n[locale][errors.fnamePolicyOwner]} />
                  )}
                </div>

                <div className="col">
                  <FormInput
                    name={BMO_CONTACT_FIELDS.POLICYOWNER_LAST_NAME}
                    label={BMOi18n[locale][compact ? 'LAST_NAME' : 'POLICYOWNER_LAST_NAME']}
                    error={showErrors && !!errors.lnamePolicyOwner}
                    placeholder="Doe"
                    required
                  />

                  {showErrors && errors.lnamePolicyOwner && (
                    <FormError name={BMO_CONTACT_FIELDS.POLICYOWNER_LAST_NAME} message={BMOi18n[locale][errors.lnamePolicyOwner]} />
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  renderSecondPolicyowner = () => {
    const {
      errors,
      locale,
      values: { showSecondPolicyOwner },
      compact,
    } = this.props;
    const { showErrors } = this.state;

    return (
      <>
        {showSecondPolicyOwner && (
          <div className="field-section policyowner-section">
            {compact && <h3>{BMOi18n[locale].POLICYOWNER_2}</h3>}

            <div className="row second-policyowner" data-private data-dd-privacy="mask">
              <div className="col">
                <FormInput
                  name={BMO_CONTACT_FIELDS.SECOND_POLICYOWNER_FIRST_NAME}
                  label={BMOi18n[locale][compact ? 'FIRST_NAME' : 'POLICYOWNER_FIRST_NAME']}
                  placeholder="John"
                  error={showErrors && !!errors.fnameSecondPolicyOwner}
                  required
                />

                {showErrors && errors.fnameSecondPolicyOwner && (
                  <FormError name={BMO_CONTACT_FIELDS.SECOND_POLICYOWNER_FIRST_NAME} message={BMOi18n[locale][errors.fnameSecondPolicyOwner]} />
                )}
              </div>

              <div className="col">
                <div className="d-flex">
                  <FormInput
                    name={BMO_CONTACT_FIELDS.SECOND_POLICYOWNER_LAST_NAME}
                    label={BMOi18n[locale][compact ? 'LAST_NAME' : 'POLICYOWNER_LAST_NAME']}
                    placeholder="Doe"
                    error={showErrors && !!errors.lnameSecondPolicyOwner}
                    required
                  />

                  <IconButton icon="ds-delete" onClick={this.deleteSecondPolicyOwner} />
                </div>

                {showErrors && errors.lnameSecondPolicyOwner && (
                  <FormError name={BMO_CONTACT_FIELDS.SECOND_POLICYOWNER_LAST_NAME} message={BMOi18n[locale][errors.lnameSecondPolicyOwner]} />
                )}
              </div>
            </div>
          </div>
        )}

        {!showSecondPolicyOwner && (
          <div className="row add-second-policyowner" data-private data-dd-privacy="mask">
            <PrimaryLink onClick={this.showSecondPolicyOwner} icon="plus">
              {BMOi18n[locale].ADD_SECOND_POLICYOWNER}
            </PrimaryLink>
          </div>
        )}
      </>
    );
  };

  onChangeAccountType = (item: RadioItem) => {
    const {
      values: { accountType },
    } = this.props;

    if (accountType !== item.value) {
      // Reset registered account dropdown values
      this.props.setFieldValue(BMO_CONTACT_FIELDS.REGISTERED_ACCOUNT_TYPE, undefined);
      this.props.setFieldValue(BMO_CONTACT_FIELDS.PROVINCE, undefined);
    }

    this.props.setFieldValue(BMO_CONTACT_FIELDS.ACCOUNT_TYPE, item.value);
  };

  renderAccountTypes = () => {
    const {
      values: { accountType },
    } = this.props;

    const items: RadioItem[] = Object.keys(ACCOUNT_TYPES).map(type => ({
      label: formatMessage(`AccountTypes.${type}`),
      value: ACCOUNT_TYPES[type],
    }));

    const selectedItem = items.find(i => i.value === accountType);

    return <RadioGroup items={items} value={selectedItem} onChange={this.onChangeAccountType} />;
  };

  onChangeRegisteredAccountType = (value: string) => {
    // Reset province when changing registered account type due to switching logic
    if (value === REGISTERED_ACCOUNT_TYPES.LIRA || value === REGISTERED_ACCOUNT_TYPES.LRSP) {
      this.props.setFieldValue(BMO_CONTACT_FIELDS.PROVINCE, undefined);
    }

    this.props.setFieldValue(BMO_CONTACT_FIELDS.REGISTERED_ACCOUNT_TYPE, value);
  };

  renderRegisteredAccountTypes = () => {
    const {
      values: { registeredAccountType },
      locale,
      errors,
    } = this.props;

    return (
      <>
        <SelectInput
          className="registered-account-types"
          placeholder={BMOi18n[locale].SELECT_REGISTERED_ACCOUNT}
          selected={registeredAccountType}
          onValueChange={this.onChangeRegisteredAccountType}
        >
          {Object.values(REGISTERED_ACCOUNT_TYPES).map((value, i) => (
            <SelectOption key={`${i}-${value}`} value={value}>
              <Tooltip delayShow={300} message={BMOi18n[locale][value]}>
                <span>{BMOi18n[locale][value]}</span>
              </Tooltip>
            </SelectOption>
          ))}
        </SelectInput>

        {errors.registeredAccountType && (
          <FormError name={BMO_CONTACT_FIELDS.REGISTERED_ACCOUNT_TYPE} message={BMOi18n[locale][errors.registeredAccountType]} />
        )}
      </>
    );
  };

  onChangeProvince = (province: string) => {
    const {
      values: { registeredAccountType },
    } = this.props;

    if (!registeredAccountType) {
      return;
    }

    const updatedRegisteredAccountType = switchRegisteredAccountType(registeredAccountType, province);

    this.props.setFieldValue(BMO_CONTACT_FIELDS.REGISTERED_ACCOUNT_TYPE, updatedRegisteredAccountType);

    this.props.setFieldValue(BMO_CONTACT_FIELDS.PROVINCE, province);
  };

  renderProvinces = () => {
    const {
      values: { province, registeredAccountType },
      locale,
    } = this.props;

    return (
      <SelectInput
        className="provinces"
        disabled={!registeredAccountType}
        placeholder={BMOi18n[locale].SELECT_PROVINCE}
        selected={province}
        onValueChange={this.onChangeProvince}
      >
        {getFilteredProvinces(registeredAccountType).map(value => (
          <SelectOption key={value} value={PROVINCES[value]}>
            {formatMessage(`Provinces.${value}`)}
          </SelectOption>
        ))}
      </SelectInput>
    );
  };

  render() {
    const {
      contact,
      errors,
      values: { accountType, ownerIsAnnuitant },
      locale,
      compact,
    } = this.props;
    const { dobInput, showErrors } = this.state;

    return (
      <div className="edit-bmo-contact">
        <Form>
          <h1>{contact._id ? BMOi18n[locale].EDIT_CLIENT : BMOi18n[locale].CREATE_CLIENT}</h1>

          <div className="container">
            <div className="field-section">
              {compact && <h3>{BMOi18n[locale].ANNUITANT}</h3>}

              <div className="row annuitant" data-private data-dd-privacy="mask">
                <FormInput
                  className="col"
                  name={BMO_CONTACT_FIELDS.ANNUITANT_FIRST_NAME}
                  label={BMOi18n[locale][compact ? 'FIRST_NAME' : 'ANNUITANT_FIRST_NAME']}
                  placeholder="David"
                  error={showErrors && !!errors.fname}
                  required
                />

                <FormInput
                  className="col"
                  name={BMO_CONTACT_FIELDS.ANNUITANT_LAST_NAME}
                  placeholder="Doe"
                  label={BMOi18n[locale][compact ? 'LAST_NAME' : 'ANNUITANT_LAST_NAME']}
                  error={showErrors && !!errors.lname}
                  required
                />
              </div>

              <div className="row date-of-birth" data-private data-dd-privacy="mask">
                <div className="col">
                  <FormInput
                    name={BMO_CONTACT_FIELDS.DOB}
                    label={BMOi18n[locale].DOB}
                    placeholder="DD/MM/YYYY"
                    inputMask={dobMask}
                    required
                    value={dobInput}
                    onValueChange={(value: string) => this.onChangeDob(BMO_CONTACT_FIELDS.DOB, value)}
                    error={showErrors && !!errors.dob}
                  />

                  {showErrors && errors.dob && <FormError name={BMO_CONTACT_FIELDS.DOB} message={BMOi18n[locale][errors.dob]} />}
                </div>

                <FormInput className="col age" label={BMOi18n[locale].AGE} disabled value={this.renderAge(dobInput)} />
              </div>
            </div>

            {this.renderSeperatePolicyowner()}

            {!ownerIsAnnuitant && this.renderSecondPolicyowner()}

            <div className="row account-details">
              <h3>{BMOi18n[locale].ACCOUNT_DETAILS}</h3>
            </div>

            <div className="row" data-private data-dd-privacy="mask">
              <div className="col account-types">
                {this.renderAccountTypes()}

                {showErrors && errors.accountType && <FormError name={BMO_CONTACT_FIELDS.ACCOUNT_TYPE} message={BMOi18n[locale][errors.accountType]} />}
              </div>

              {accountType === ACCOUNT_TYPES.REGISTERED && (
                <div className="col dropdown-options">
                  {this.renderRegisteredAccountTypes()}

                  {this.renderProvinces()}
                </div>
              )}
            </div>
          </div>
        </Form>

        <div className="edit-bmo-contact-actions">
          <CancelButton onClick={this.onCancel} />

          <PrimaryButton onClick={this.onSubmit}>
            <FormattedMessage id="Contacts.Save" />
          </PrimaryButton>
        </div>
      </div>
    );
  }
}

const EditBMOContactSchema = Yup.object()
  .shape({
    fname: Yup.string().required(),
    lname: Yup.string().required(),
    dob: Yup.date().typeError(BMOFormErrorKeys.INVALID_DATE).required(BMOFormErrorKeys.DOB_REQUIRED).max(new Date(), BMOFormErrorKeys.INVALID_DATE),
    fnamePolicyOwner: Yup.string().when(BMO_CONTACT_FIELDS.OWNER_IS_ANNUITANT, (ownerIsAnnuitant: boolean) => {
      if (!ownerIsAnnuitant) {
        return Yup.string().required(BMOFormErrorKeys.MISSING_POLICYOWNER_FNAME);
      }

      return Yup.string();
    }),
    lnamePolicyOwner: Yup.string().when(BMO_CONTACT_FIELDS.OWNER_IS_ANNUITANT, (ownerIsAnnuitant: boolean) => {
      if (!ownerIsAnnuitant) {
        return Yup.string().required(BMOFormErrorKeys.MISSING_POLICYOWNER_LNAME);
      }

      return Yup.string();
    }),
    fnameSecondPolicyOwner: Yup.string().when(
      [BMO_CONTACT_FIELDS.OWNER_IS_ANNUITANT, BMO_CONTACT_FIELDS.SHOW_SECOND_POLICYOWNER],
      (ownerIsAnnuitant: boolean, showSecondPolicyOwner: boolean) => {
        if (!ownerIsAnnuitant && showSecondPolicyOwner) {
          return Yup.string().required(BMOFormErrorKeys.MISSING_POLICYOWNER_FNAME);
        }

        return Yup.string();
      },
    ),
    lnameSecondPolicyOwner: Yup.string().when(
      [BMO_CONTACT_FIELDS.OWNER_IS_ANNUITANT, BMO_CONTACT_FIELDS.SHOW_SECOND_POLICYOWNER],
      (ownerIsAnnuitant: boolean, showSecondPolicyOwner: boolean) => {
        if (!ownerIsAnnuitant && showSecondPolicyOwner) {
          return Yup.string().required(BMOFormErrorKeys.MISSING_POLICYOWNER_LNAME);
        }

        return Yup.string();
      },
    ),
    accountType: Yup.string().required(BMOFormErrorKeys.ACCOUNT_TYPE_REQUIRED),
    province: Yup.string(),
    registeredAccountType: Yup.string().when(BMO_CONTACT_FIELDS.ACCOUNT_TYPE, (accountType: string) => {
      if (accountType === ACCOUNT_TYPES.REGISTERED) {
        return Yup.string().required(BMOFormErrorKeys.MISSING_REGISTERED_ACCOUNT);
      }
    }),
  })
  .test('validation', '', function test(value) {
    if (!value) return true;
    const { dob, registeredAccountType } = value;

    const baseAge = getBaseAge(dob);

    if (!baseAge) {
      return true;
    }

    switch (true) {
      case baseAge > 90: {
        return this.createError({
          path: BMO_CONTACT_FIELDS.DOB,
          message: BMOFormErrorKeys.MAX_AGE_90,
        });
      }
      case baseAge > 71 && !!maxAge71Accounts.find(a => a === registeredAccountType): {
        return this.createError({
          path: BMO_CONTACT_FIELDS.REGISTERED_ACCOUNT_TYPE,
          message: BMOFormErrorKeys.INVALID_ACCOUNT_TYPE_AGE_71,
        });
      }
      default:
        return true;
    }
  });

const mapStateToProps = (state: ReduxState): StateProps => ({
  locale: state.intl.locale,
});

const FormikWrapper = withFormik<ParentProps, FormValues>({
  validateOnChange: true,
  validateOnBlur: true,
  validationSchema: EditBMOContactSchema,
  mapPropsToValues: (initialValues: ParentProps) => ({
    fname: initialValues.contact.fname || '',
    lname: initialValues.contact.lname || '',
    dob: initialValues.contact.dob,
    ownerIsAnnuitant: initialValues.contact.ownerIsAnnuitant !== undefined ? initialValues.contact.ownerIsAnnuitant : true,
    fnamePolicyOwner: initialValues.contact.fnamePolicyOwner,
    lnamePolicyOwner: initialValues.contact.lnamePolicyOwner,
    fnameSecondPolicyOwner: initialValues.contact.fnameSecondPolicyOwner,
    lnameSecondPolicyOwner: initialValues.contact.lnameSecondPolicyOwner,
    accountType: initialValues.contact.accountType,
    province: initialValues.contact.province,
    registeredAccountType: initialValues.contact.registeredAccountType,
    showSecondPolicyOwner: (!!initialValues.contact.fnameSecondPolicyOwner && !!initialValues.contact.lnameSecondPolicyOwner) || false,
    type: BMO_CONTACT_TYPE,
  }),
  handleSubmit: (values, formikBag) => {
    const copy = { ...values };
    delete copy.showSecondPolicyOwner;
    const contact: Partial<GenericContact> = { ...copy, _id: formikBag.props.contact._id };

    formikBag.props
      .saveContact(contact)
      .then((c: GenericContact) => {
        notification.success(contact._id ? BMOi18n[formikBag.props.locale].EDIT_SUCCESS : BMOi18n[formikBag.props.locale].CREATE_SUCCESS);
        formikBag.props.onSave(c);
      })
      .catch(showError);
  },
})(EditBMOContact);

export default connect(mapStateToProps, {
  saveContact,
})(FormikWrapper);
